/* eslint-disable */
// useNotificationDeepLink — the wire from ?n=<notification id> to a destination.
//
// The email button said "Open the lens" and landed on the portal picker: the
// LABEL was resolved from the item and the DESTINATION was not. entityRoutes.js
// opens its own file with the rule that answers this — never render a button
// that cannot work — so the fix is to build the link, not to relabel the button.
//
// ⚠️ THIS IS A FIFTH CONSUMER OF entityRoutes, NOT A SECOND COPY OF IT. Every
// question about where something goes is answered there; every question about
// what an attachment IS is answered by useAttachmentEntities, under the reader's
// own RLS. Nothing here re-implements either. A second resolver drifts from the
// first, and then the email opens somewhere the drawer does not.
//
// ⚠️ THE PARAMETER IS THE NOTIFICATION, NOT THE ITEM. Three reasons, and the
// third is the one that matters:
//   1. the app already turns a notification into a destination;
//   2. arriving from the email is proof of reading, so the same handler stamps
//      read_at — on the strength of the open returning true, exactly as the four
//      in-app call sites do;
//   3. ACCESS CONTROL COMES FREE. The id is a pointer into a table that already
//      guards itself: RLS on notification_recipients means a forwarded link
//      SELECTs nothing for a non-recipient. It is not a secret and must not be
//      treated as one — no token, no signature, no expiry.
//
// ⚠️ A LINK IS NEVER DEAD. A plain announcement has no entity and no attachment;
// it opens the drawer rather than doing nothing or bouncing to the picker with
// no explanation. First match wins: attachment, then the legacy entity pair,
// then the drawer.

import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { readAttachments, legacyAttachment } from '../utils/attachmentRegistry';
import { useAttachmentEntities } from './useAttachmentEntities';
import { useNotifications } from './useNotifications';
import { useNotificationsStore } from '../stores/notificationsStore';
import {
  canOpenAttachment, openAttachment, canOpenNotification, openNotification,
} from '../utils/entityRoutes';

// ⚠️ CAPTURED AT MODULE LOAD, WHICH IS EARLIER THAN ANY COMPONENT. Somebody
// opening this email on a Monday morning is SIGNED OUT: they land on
// EntryLoginScreen, sign in, and only then does the app shell mount. A value
// read in a component initialiser would be read after that journey; this one is
// taken when the bundle evaluates, before React renders anything and before
// anything can rewrite the query string. Module state survives the login screen
// because nothing unmounts the module.
//
// The brief specified capturing it in Root's useState and threading it through
// App. That works, but App.js is on the GOV1 grandfather list (Conv #163) and
// may shrink, never grow — an import, a capture, a prop and a call site is four
// lines it cannot afford, and H2 blocks the commit. AppOverlays exists for
// exactly this reason (see its own header) and is where this is consumed. Same
// timing, same behaviour, zero lines added to App.js.
const DEEP_LINK_ID = (() => {
  try { return new URLSearchParams(window.location.search).get('n'); } catch (_) { return null; }
})();

export function useNotificationDeepLink() {
  const startedRef = useRef(false);     // fetch exactly once
  const settledRef = useRef(false);     // navigate exactly once
  const [row, setRow] = useState(null); // the recipient row, or {} for "nothing to open"
  const notificationId = DEEP_LINK_ID;

  const setShowDrawer = useNotificationsStore((st) => st.setShowDrawer);
  const { markRead } = useNotifications();

  // ---- 1. the row, once, and only when signed in ---------------------------
  // ⚠️ NO DATABASE READ ON BOOT FOR A SIGNED-OUT READER, and the guard is
  // structural rather than a flag: this hook is called from AppOverlays, which
  // App renders, which Root reaches only past `if (!user) return
  // <EntryLoginScreen>`. Being called at all IS the authentication — the same
  // reasoning NotificationHost and ActivityDrawer use for taking no user prop.
  useEffect(() => {
    if (!notificationId || startedRef.current) return;
    startedRef.current = true;
    let alive = true;

    (async () => {
      let found = {};
      try {
        const { data, error } = await supabase
          .from('notification_recipients')
          .select('id, read_at, notifications ( id, type_key, entity_type, entity_id, payload )')
          .eq('notification_id', notificationId)
          .maybeSingle();
        // A garbage id is a 400 from PostgREST and a non-recipient is simply no
        // row. Neither is exceptional and neither is worth a Sentry event: both
        // mean "nothing to open", and §2.2's third route handles them the same.
        if (error) console.warn('[notifications] deep link not readable:', error.message);
        if (data && data.notifications) found = data;
      } catch (e) {
        Sentry.captureException(e, { tags: { source: 'useNotificationDeepLink' } });
      }
      if (alive) setRow(found);
    })();

    return () => { alive = false; };
  }, [notificationId]);

  // ---- 2. what it points at ------------------------------------------------
  const refs = useMemo(() => {
    const n = row && row.notifications;
    if (!n) return [];
    // Same precedence as NotificationAttachments: P6 attachments win outright,
    // the legacy entity_type/entity_id pair is the fallback. Only the first is
    // ever navigated to — a button goes to one place.
    const fromPayload = readAttachments(n.payload);
    if (fromPayload.length) return [fromPayload[0]];
    const legacy = legacyAttachment(n.entity_type, n.entity_id);
    return legacy ? [legacy] : [];
  }, [row]);

  // Called unconditionally with [] when there is nothing to resolve — the hook
  // rules do not bend for an early return, and an empty list costs no request.
  const { byKey, state } = useAttachmentEntities(refs);

  // ---- 3. go, exactly once -------------------------------------------------
  useEffect(() => {
    if (!row || settledRef.current) return;
    // Wait for the code: requestOpenLens and requestOpenLtd navigate by lens_no
    // and ltd_code, which are only known once the row is fetched under the
    // reader's own RLS. canOpenAttachment refuses a lens without one.
    if (refs.length && state !== 'ready') return;
    settledRef.current = true;

    const n = row.notifications;
    let opened = false;

    if (n) {
      if (refs.length) {
        const r = refs[0];
        const entity = byKey[r.kind + '|' + r.id + (r.byEntityId ? '|e' : '')];
        if (entity && canOpenAttachment(entity)) opened = openAttachment(entity);
      }
      if (!opened && canOpenNotification(n)) opened = openNotification(n);
      // Only a real arrival stamps the receipt. When it falls through to the
      // drawer the row stays unread on purpose: the drawer shows it as unread
      // and the reader's own click marks it, which is the existing behaviour
      // rather than a second one.
      if (opened && !row.read_at) { try { markRead(row.id); } catch (_) {} }
      if (!opened) setShowDrawer(true);
    }

    // Clear it either way, so a refresh does not re-navigate and the id does not
    // sit in the address bar. replaceState is not routing (Convention #136) —
    // it rewrites the query string and nothing observes it.
    try { window.history.replaceState({}, '', window.location.pathname); } catch (_) {}
  }, [row, refs, state, byKey, markRead, setShowDrawer]);
}

export default useNotificationDeepLink;
